import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  Observable,
  Subject,
  defer,
  filter,
  forkJoin,
  map,
  of,
  race,
  switchMap,
  take,
  timer,
  withLatestFrom,
} from 'rxjs';

import {
  addInvoiceList,
  addSelectedPaymentList,
  getInvoiceSummary,
  getPatientWallet,
  selectInvoiceSummary,
  selectInvoiceSummaryRequestStatus,
  setCurrentInvoice,
} from '@patient-ui/patient-web/store';
import {
  ORBI_BILLING_NAVIGATION_DELAY_MS,
  ORBI_RETURN_TYPE,
  isReturnType,
} from '@patient-ui/shared/constants';
import { OrbiMessageType, RequestStatus } from '@patient-ui/shared/enums';
import { Invoice, OrbiMessage, PortalUser } from '@patient-ui/shared/models';
import {
  additionalInvoiceNumbers,
  matchInvoicesByNumber,
} from '@patient-ui/shared/utils';

import { LayoutService } from '../../shared/services/layout.service';

/** The portal page a billing return type hands off to, plus what it needs. */
interface OrbiBillingDestination {
  /** The `returnType` this destination answers to (matched case-insensitively). */
  readonly returnType: string;
  /** Router commands for the destination page. */
  readonly commands: string[];
  /** Query params the destination itself requires (e.g. `navigatedFrom=RU`). */
  readonly queryParams?: Record<string, string>;
  /**
   * Whether the page acts on the SELECTED-invoice list in the store. These are
   * the destinations whose invoice numbers must be resolved to real invoices
   * before navigating — the agent sends numbers only, and a checkout page with
   * nothing selected is a dead end.
   */
  readonly seedsSelection: boolean;
  /**
   * Whether the page reads the store's CURRENT invoice (`state.invoice`) rather
   * than — or as well as — the selected-invoice list.
   *
   * Only Update Insurance does. `addSelectedPaymentList` fills
   * `state.selectedInvoices`, which is what the checkout and payment-plan pages
   * read, but the insurance page reads `state.invoice` for the invoice number,
   * balance, date of service and billing address it displays and submits
   * (`populateInvoice`) — and it EJECTS to the Billing list on arrival when that
   * invoice is empty (PCSA-13559: `state.invoice.number === '' &&
   * navigatedFrom === 'RU' && !insuranceForm.valid`, and a freshly built form is
   * always invalid, so the eject was unconditional). Seeding only the selection
   * list therefore navigated correctly and then bounced the patient straight back
   * out, which read as "the agent does not navigate to the Insurance page".
   *
   * The Billing page's own hand-off into the same route has always seeded this —
   * `PortalInvoicesInsuranceListComponent.updateInsurance()` dispatches an invoice
   * into `state.invoice` before navigating. This flag is that step, for the agent.
   */
  readonly seedsCurrentInvoice: boolean;
  /** Whether the page should open with the patient's saved payment methods. */
  readonly needsWallet: boolean;
  /**
   * Whether the invoice numbers travel as `?invoices=` for the page to
   * pre-select (the Billing list reads them from the URL, not from the store).
   */
  readonly preselectsInQuery: boolean;
  /**
   * Whether the invoice numbers travel as `?invoices=` so the page can rebuild
   * the store state above for ITSELF, without the agent.
   *
   * Everything seeded here lives in memory, so it does not survive a reload: on
   * refresh `state.invoice` is empty again and Update Insurance ejects to the
   * Billing list, exactly as it did before `seedsCurrentInvoice` existed
   * (PCSA-13312: "when I refresh it's taking me to the invoice page — it should
   * persist in the same page even during refresh"). The URL is the only part of
   * the hand-off the browser keeps, so for those pages it has to say WHICH bills
   * and not merely that an agent sent the patient (`navigatedFrom=RU`).
   *
   * Separate from `preselectsInQuery` because they are different contracts on the
   * same param: the Billing list ticks checkboxes with it and needs nothing else,
   * while a restoring page re-runs the whole seeding path. A destination that
   * seeds store state and can be reloaded wants this one.
   */
  readonly restoresFromQuery: boolean;
}

/** A billing turn accepted for hand-off, reduced to what the flow acts on. */
interface OrbiBillingHandoff {
  readonly destination: OrbiBillingDestination;
  readonly invoiceNumbers: string[];
}

/**
 * Hands a billing turn off to the portal page it names (PCSA-13416).
 *
 * The Billing agent answers `"Sure — here's where you can pay that bill"` in
 * TEXT and expresses the destination as a `returnType`; the text alone is not the
 * answer, the page is. QA reported the web client rendering the sentence and then
 * leaving the user on the dashboard (`returnType: PayABill`, no navigation) —
 * that is this story, which had not been built: the parser already routes all six
 * billing types to `billing-card` and attaches `data.invoices`, and only
 * `FindABill` had a consumer (its card, PCSA-13419).
 *
 * Per the ACs the flow is: the response renders through the normal text path →
 * a ~1.5s pause so it can be read ([ORBI_BILLING_NAVIGATION_DELAY_MS]) → the chat
 * panel closes → the portal navigates:
 *
 * | returnType       | destination                            |
 * | ---------------- | -------------------------------------- |
 * | `PayABill`       | `/portal/invoices/portalPayment`       |
 * | `PaymentPlan`    | `/portal/invoices/paymentPlan`         |
 * | `UpdateInsurance`| `/invoices/insurance?navigatedFrom=RU` |
 * | `Invoices`       | `/portal/invoices/billing`             |
 * | `InvoiceHistory` | `/portal/invoices/history/list`        |
 *
 * The invoice numbers from the response travel with the user (one or many). The
 * agent returns NUMBERS, never invoice objects, so the three destinations that
 * act on a bill resolve them against the invoice summary and seed the portal's
 * own selection (`addSelectedPaymentList`) — the same state the Billing page
 * hands those pages. Only invoices the agent actually named are carried: if none
 * of them resolve, the user goes to the invoice LIST to choose, rather than to a
 * checkout page seeded with some other bill.
 *
 * Seeding the selection is necessary but not always sufficient: a destination
 * states what it reads, and the seeding follows the flags rather than one
 * one-size dispatch. Update Insurance reads the store's CURRENT invoice
 * (`state.invoice`) and ejects to the Billing list when it is empty, so a
 * selection-only hand-off navigated there and was bounced straight back — QA read
 * that as no navigation at all (PCSA-13559, fixed via `seedsCurrentInvoice`). The
 * hand-off is therefore only complete when the destination's own arrival guard is
 * satisfied; the spec asserts that by folding the dispatched actions through the
 * REAL invoice reducer, so a new destination cannot pass on dispatch count alone.
 *
 * This is a WEB-ONLY behavior: the mobile app renders the same billing turns as
 * text and has no equivalent hand-off, so there is no parity target to match.
 *
 * The timer lives here, in a root service, rather than in the thread component:
 * closing the panel (step 3) destroys the thread, which would tear its own
 * subscription down mid-flow. The thread is still what STARTS the flow, so an
 * answer that arrives after the user has closed the panel and moved on is
 * ignored instead of yanking them onto a billing page. A panel closed by hand
 * inside the 1.5s window does not cancel a hand-off already under way.
 */
@Injectable({ providedIn: 'root' })
export class OrbiBillingNavigationService {
  /** The open-invoice list — every destination's fallback, and `Invoices`. */
  private static readonly INVOICE_LIST_ROUTE = '/portal/invoices/billing';

  /**
   * The destination table. `FindABill` is deliberately absent: it renders an
   * interactive card (PCSA-13419) whose lookup decides where the user goes, so
   * there is nothing to navigate to on arrival.
   *
   * Declared AFTER `INVOICE_LIST_ROUTE` because it reads it: static fields
   * initialize in source order.
   */
  private static readonly DESTINATIONS: readonly OrbiBillingDestination[] = [
    {
      returnType: ORBI_RETURN_TYPE.PayABill,
      commands: ['/portal/invoices/portalPayment'],
      seedsSelection: true,
      // Checkout reads `state.selectedInvoices` (and ejects on an EMPTY list, not
      // on an empty `state.invoice`), so the selection alone is enough.
      seedsCurrentInvoice: false,
      needsWallet: true,
      preselectsInQuery: false,
      // Checkout loses its selection on refresh too, and bounces to the invoice
      // list for the same reason. Not restored here because a reload mid-checkout
      // is not a hand-off to replay: the page is a step in a wizard that also
      // holds an amount, a wallet selection and, past the first step, a payment in
      // progress — re-seeding only the bills would drop the patient back at step
      // one with their entries gone, which is what the invoice list already does
      // more honestly. Left for its own ticket rather than half-done here.
      restoresFromQuery: false,
    },
    {
      returnType: ORBI_RETURN_TYPE.PaymentPlan,
      commands: ['/portal/invoices/paymentPlan'],
      seedsSelection: true,
      // As checkout: the plan page's own guard is on the selected list.
      seedsCurrentInvoice: false,
      // The payment-plan flow fetches the wallet at its select-payment-method
      // step, exactly as the Billing page's own hand-off relies on.
      needsWallet: false,
      preselectsInQuery: false,
      // As checkout: a multi-step flow, so see PayABill above.
      restoresFromQuery: false,
    },
    {
      returnType: ORBI_RETURN_TYPE.UpdateInsurance,
      commands: ['/invoices/insurance'],
      queryParams: { navigatedFrom: 'RU' },
      seedsSelection: true,
      // The one destination that reads `state.invoice` (PCSA-13559).
      seedsCurrentInvoice: true,
      // Insurance takes no payment: the page has no wallet control, and its
      // success path returns to Billing rather than to checkout. Fetching the
      // wallet here was a needless authenticated call on every hand-off.
      needsWallet: false,
      preselectsInQuery: false,
      // A single form, and the only destination whose arrival guard ejects on
      // state this service seeded — so it is also the only one a refresh throws
      // out of. It restores (PCSA-13312).
      restoresFromQuery: true,
    },
    {
      returnType: ORBI_RETURN_TYPE.Invoices,
      commands: [OrbiBillingNavigationService.INVOICE_LIST_ROUTE],
      seedsSelection: false,
      seedsCurrentInvoice: false,
      needsWallet: false,
      preselectsInQuery: true,
      // Nothing to restore: it pre-selects from the URL, so a refresh re-reads
      // the same param and lands in the same place already.
      restoresFromQuery: false,
    },
    {
      returnType: ORBI_RETURN_TYPE.InvoiceHistory,
      commands: ['/portal/invoices/history/list'],
      seedsSelection: false,
      seedsCurrentInvoice: false,
      needsWallet: false,
      preselectsInQuery: false,
      // Takes no payload at all, so a refresh is already a no-op.
      restoresFromQuery: false,
    },
  ];

  /** The summary is fetched for ALL patients, as the Billing page fetches it. */
  private static readonly ALL_PATIENTS: PortalUser = {
    id: 0,
    displayName: 'All Patients',
  };

  /**
   * How long the invoice lookup may hold the hand-off up. It runs DURING the
   * 1.5s pause, so it normally costs nothing; this only bounds the case where the
   * summary call never settles, which would otherwise strand the user in the chat
   * with an answer that told them they were about to be taken somewhere.
   */
  private static readonly LOOKUP_TIMEOUT_MS = 6000;

  private readonly handoffs = new Subject<OrbiBillingHandoff>();

  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly layoutService: LayoutService
  ) {
    // `switchMap`: a newer billing turn supersedes one still counting down. The
    // user asked a second question — the first answer's destination is stale, and
    // navigating to both would flash the wrong page on the way to the right one.
    this.handoffs
      .pipe(
        switchMap((handoff) =>
          forkJoin([
            timer(ORBI_BILLING_NAVIGATION_DELAY_MS),
            this.resolveInvoices(handoff),
          ]).pipe(map(([, invoices]) => ({ handoff, invoices })))
        )
      )
      .subscribe(({ handoff, invoices }) => this.navigate(handoff, invoices));
  }

  /**
   * Take a just-answered turn, and hand off if it names a billing destination.
   *
   * Non-billing turns, the `FindABill` card and guardrail turns are no-ops — a
   * guardrail answer is a refusal, so it must not also move the user. Returns
   * whether the turn was accepted, which is what makes the decision testable
   * (and, for the thread, observable) without waiting out the delay.
   */
  handleAnsweredTurn(message: OrbiMessage): boolean {
    if (
      message.type !== OrbiMessageType.Llm ||
      message.route !== 'billing-card' ||
      message.guardrailTriggered
    ) {
      return false;
    }

    const returnType = message.structuredData?.returnType;
    const destination = OrbiBillingNavigationService.DESTINATIONS.find(
      (entry) => isReturnType(returnType, entry.returnType)
    );
    if (!destination) {
      return false;
    }

    this.handoffs.next({
      destination,
      invoiceNumbers: message.structuredData?.invoiceNumbers ?? [],
    });
    return true;
  }

  /**
   * Resolve the agent's invoice NUMBERS to the portal's own invoices.
   *
   * Only for destinations that act on a bill; `Invoices` / `InvoiceHistory` need
   * no lookup (the list pre-selects from the URL and history takes no payload).
   * The summary is re-fetched rather than read from whatever the store happens to
   * hold: the user may never have opened Billing this session, and a bill the
   * agent just described could be newer than a cached summary.
   */
  private resolveInvoices(handoff: OrbiBillingHandoff): Observable<Invoice[]> {
    if (!handoff.destination.seedsSelection || !handoff.invoiceNumbers.length) {
      return of([]);
    }

    const lookup$ = defer(() => {
      this.store.dispatch(
        getInvoiceSummary({
          payload: OrbiBillingNavigationService.ALL_PATIENTS,
        })
      );
      return this.store.select(selectInvoiceSummaryRequestStatus);
    }).pipe(
      // The reducer flips the status to Pending inside the dispatch above, so the
      // first value seen here is that Pending — waiting for a SETTLED status can
      // never latch onto the previous request's result.
      filter(
        (status) =>
          status === RequestStatus.Success || status === RequestStatus.Failure
      ),
      take(1),
      withLatestFrom(this.store.select(selectInvoiceSummary)),
      map(([, summary]) =>
        matchInvoicesByNumber(summary, handoff.invoiceNumbers)
      )
    );

    return race(
      lookup$,
      timer(OrbiBillingNavigationService.LOOKUP_TIMEOUT_MS).pipe(
        map(() => [] as Invoice[])
      )
    );
  }

  /**
   * Seed what the destination reads, close the panel, then route — the order
   * every other Orbi hand-off uses (mobile's `setOverlayOpen(false)` before
   * navigating), so the user lands on the page unobstructed.
   */
  private navigate(handoff: OrbiBillingHandoff, invoices: Invoice[]): void {
    const { destination } = handoff;

    // Asked to pay/insure a bill we could not resolve: the invoice list is where
    // the user picks one, and it is a real page rather than an empty checkout.
    if (destination.seedsSelection && !invoices.length) {
      this.layoutService.setOrbiPanelOpen(false);
      this.router.navigate([OrbiBillingNavigationService.INVOICE_LIST_ROUTE]);
      return;
    }

    if (invoices.length) {
      this.store.dispatch(addSelectedPaymentList({ payload: invoices }));
    }

    // The current invoice, for the one page that reads it (PCSA-13559). MUST come
    // after the selection above: `setCurrentInvoice` promotes a match out of
    // `state.selectedInvoices`, so with the list still empty it is a no-op and the
    // page would eject exactly as before.
    //
    // The primary invoice is the FIRST the agent named — the same convention the
    // Billing modal's own hand-off uses (`selectedInvoiceArray[0]` is submitted,
    // the rest go to the additional-invoices field). Reversing those two would put
    // the bill the patient asked about into a free-text field and submit a
    // different one.
    if (destination.seedsCurrentInvoice && invoices.length) {
      this.store.dispatch(
        setCurrentInvoice({ invoiceNumber: invoices[0].number })
      );
      const additional = additionalInvoiceNumbers(invoices);
      if (additional.length) {
        this.store.dispatch(addInvoiceList({ payload: additional }));
      }
    }

    if (destination.needsWallet) {
      this.store.dispatch(getPatientWallet());
    }

    const queryParams: Record<string, string> = {
      ...(destination.queryParams ?? {}),
    };
    // The invoice numbers in the URL. Two different reasons, both of which want the
    // RESOLVED numbers rather than the ones the agent said:
    //
    //  - `preselectsInQuery` — the Billing list reads them from the URL to tick its
    //    own checkboxes, since it holds no selection of its own.
    //  - `restoresFromQuery` — the store is in memory, so a REFRESH on the
    //    destination empties `state.invoice` and Update Insurance ejects to the
    //    Billing list (PCSA-13312: "when I refresh it's taking me to the invoice
    //    page; it should persist in the same page even during refresh"). The page
    //    rebuilds its own state from these numbers instead, so the URL has to be a
    //    complete description of the hand-off and not just a flag saying one
    //    happened.
    //
    // Resolved, not requested: a number the summary did not match was already
    // dropped from `invoices`, and carrying it in the URL would have the reloaded
    // page try the same lookup and fail it again — with the primary bill possibly
    // shifting to whichever number happened to resolve.
    const resolvedNumbers = invoices.map((invoice) => invoice.number);
    const carriesNumbers =
      destination.preselectsInQuery || destination.restoresFromQuery;
    if (carriesNumbers) {
      const numbers = resolvedNumbers.length
        ? resolvedNumbers
        : handoff.invoiceNumbers;
      if (numbers.length) {
        queryParams['invoices'] = numbers.join(',');
      }
    }

    this.layoutService.setOrbiPanelOpen(false);
    this.router.navigate(
      destination.commands,
      Object.keys(queryParams).length ? { queryParams } : {}
    );
  }
}
